import { useRouter } from "next/router";
import Image from "next/image";
import { useWindowSize } from "@/shared/hooks/useWindowSize";
import { ReactElement, useCallback, useEffect, useState } from "react";
import Head from "next/head";
import { MainLayout } from "@/containers/layout/NowLayout";
import PropertySearchPage from "@/components/landing-new/PropertySearch/PropertySearch";
import GotQuestions from "@/components/landing-new/PropertySearch/GotQuestions/GotQuestions";
import KnowYourBudget from "@/components/landing-new/PropertySearch/KnowYourBudget/KnowYourBudget";
import Neighbourhood, {
  NeighbourhoodSlug,
} from "@/components/properties/Neighbourhood/Neighbourhood";
import OwnYourFutureHome from "@/components/properties/OwnYourFutureHome/OwnYourFutureHome";
import Banner1 from "@/components/properties/Banner1";
import GotQuestionsAboutNeighbourhood from "@/components/properties/GotQuestionsAboutNeighbourhood";
import BenefitsOfKeyzyNow from "@/components/landing-new/BenefitsOfKeyzyNow";
import WandsworthBrochure from "@/components/properties/WandsworthBrochure/WandsworthBrochure";
import Link from "next/link";
import routes from "@/constants/routes";
import Screen from "@/constants/screens";
import { ARTICLES_DEFAULT_POPULATE } from "@/components/blog/singleArticle/SingleArticle";
import { getArticles, getNeighbourhood } from "@/services/cms/api";
import NeighbourhoodArticles from "@/components/properties/NeighbourhoodArticles";
import { Article } from "@/services/cms/apiTypes";
interface PropertiesProps {
  hasNeighbourhoodContent: boolean;
  hasDevelopmentAd: boolean;
  isDevelopmentBookable: boolean;
  ownYourFutureHome: string;
}

const Properties = ({
  hasNeighbourhoodContent,
  hasDevelopmentAd,
  isDevelopmentBookable,
  ownYourFutureHome = "",
}: PropertiesProps) => {
  const router = useRouter();
  const { search_term, status } = router.query;
  const { width } = useWindowSize();
  const [articles, setArticles] = useState<Article[]>([]);
  // State for metadata with proper fallback
  const searchTerm = typeof search_term === "string" ? search_term : search_term?.[0];
  const metadata = searchTerm
    ? {
        title: `Homes in ${
          searchTerm.charAt(0).toUpperCase() + searchTerm.slice(1)
        } for rent to buy with Keyzy`,
        description: `Find homes for rent to buy in ${searchTerm}. Rent now, buy later with Keyzy's flexible home ownership options.`,
        canonicalUrl: `https://www.keyzy.com/properties/${searchTerm}`,
      }
    : {
        title: "Homes for rent to buy with Keyzy",
        description:
          "Find homes for rent to buy in your area. Rent now, buy later with Keyzy's flexible home ownership options.",
        canonicalUrl: "https://www.keyzy.com/properties",
      };

  const getArticle = useCallback(async () => {
    if (!search_term) return;
    const article = await getArticles({
      sort: "addedOn:desc",
      populate: ARTICLES_DEFAULT_POPULATE,
      filters: {
        neighbourhoods: {
          slug: search_term,
        },
      },
      pagination: {
        pageSize: 2,
        page: 1,
      },
    });
    setArticles(article.data);
  }, [search_term]);

  useEffect(() => {
    getArticle();
  }, [getArticle]);

  const renderLocationSpecificContent = () => {
    if (search_term === "wandsworth") {
      return <WandsworthBrochure />;
    }
    return (
      <>
        <OwnYourFutureHome
          ownYourFutureHome={ownYourFutureHome}
          neighbourhood={
            (search_term as string).charAt(0).toUpperCase() + (search_term as string).slice(1)
          }
        />
        <Banner1 />
        <Neighbourhood neighbourhood={search_term as NeighbourhoodSlug} />
        {/* <section className="px-4 mt-20 bg-white overflow-hidden md:mx-6 lg:px-0 h-6 relative">
                    <Image
                        src={`/landing/${width < Screen.Tablet_768 ? "mobile-doodle.svg" : "tablet-doodle.svg"}`}
                        alt="doodle"
                        layout="fill"
                        objectFit="contain"
                    />
                </section> */}
        {articles.length > 0 && (
          <NeighbourhoodArticles articles={articles} searchTerm={searchTerm as string} />
        )}
        {hasDevelopmentAd && <BenefitsOfKeyzyNow hideDivider={false} />}
        {hasDevelopmentAd && renderDivider()}
        <GotQuestionsAboutNeighbourhood neighbourhood={search_term as string} />
      </>
    );
  };

  const renderSignUpSection = () => {
    if (width > Screen.Tablet_1024) {
      return (
        <div className="sticky bottom-0 z-50 flex py-2 lg:py-6 px-5 lg:pl-0 lg:pr-[100px] text-2xl font-bold text-white bg-landing-blue">
          <div className="flex-1 text-base font-normal text-right">
            <span className="hidden lg:inline text-2xl font-bold">
              Request a viewing in <span className="capitalize">{searchTerm}</span>
            </span>
            <Link
              href={routes.bookNeighbourhoodViewing.bookNeighbourhoodViewing({
                slug: searchTerm as string,
              })}
            >
              <a className="inline-block text-center align-middle text-base font-bold text-white rounded-md bg-landing-cherry lg:w-[230px] h-[42px] lg:ml-4 pt-2 w-full">
                <span className="hidden lg:inline font-base font-bold">Apply</span>
              </a>
            </Link>
          </div>
        </div>
      );
    }
    if (width <= Screen.Tablet_1024) {
      return (
        <>
          <div className="sticky bottom-[env(safe-area-inset-bottom,0)] z-50  px-4 text-2xl font-bold text-center text-white bg-landing-blue py-7">
            <div className="hidden md:inline-block md:w-2/3">
              Request a viewing in <span className="capitalize">{searchTerm}</span>
            </div>
            <div className="md:w-1/3 hidden md:inline-block">
              <Link
                href={routes.bookNeighbourhoodViewing.bookNeighbourhoodViewing({
                  slug: searchTerm as string,
                })}
              >
                <a className="w-full inline-block text-center align-middle text-base font-bold text-white rounded-md bg-landing-cherry h-12 py-3">
                  Apply
                </a>
              </Link>
            </div>
            <div className="w-full md:hidden">
              <Link
                href={routes.bookNeighbourhoodViewing.bookNeighbourhoodViewing({
                  slug: searchTerm as string,
                })}
              >
                <a className="w-full z-50  inline-block text-center align-middle text-base font-bold text-white rounded-md bg-landing-cherry h-12 py-3">
                  Request a viewing in <span className="capitalize">{searchTerm}</span>
                </a>
              </Link>
            </div>
          </div>
        </>
      );
    }
  };
  const renderDivider = () => {
    return (
      <section className="px-4 mt-20 bg-white overflow-hidden md:mx-6 lg:px-0 h-6 relative">
        <Image
          src={`/landing/${width < Screen.Tablet_768 ? "mobile-doodle.svg" : "tablet-doodle.svg"}`}
          alt="doodle"
          layout="fill"
          objectFit="contain"
        />
      </section>
    );
  };
  return (
    <>
      <Head>
        <title>{metadata.title}</title>
        <link rel="canonical" href={metadata.canonicalUrl} key="canonical" />
        <meta name="description" content={metadata.description} key="description" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" key="twcard" />
        <meta name="twitter:creator" content="@keyzyhomes" key="twhandle" />
        <meta name="twitter:title" content={metadata.title} key="twtitle" />
        <meta name="twitter:description" content={metadata.description} key="twdescription" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" key="ogtype" />
        <meta property="og:url" content={metadata.canonicalUrl} key="ogurl" />
        <meta property="og:title" content={metadata.title} key="ogtitle" />
        <meta property="og:description" content={metadata.description} key="ogdescription" />
        <meta property="og:site_name" content="Keyzy" key="ogsitename" />
        <meta property="og:locale" content="en_GB" key="oglocale" />
        <meta
          property="og:image"
          content="/properties/meta-og-keyzy-properties.png"
          key="ogimage"
        />
        <meta property="og:image:width" content="1200" key="ogimagewidth" />
        <meta property="og:image:height" content="630" key="ogimageheight" />
        <meta property="og:image:alt" content="Keyzy Rent to Buy Properties" key="ogimagealt" />

        {/* Additional Meta Tags */}
        <meta name="viewport" content="width=device-width, initial-scale=1" key="viewport" />
        <meta name="robots" content="index, follow" key="robots" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://keyzy.com/" },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Properties",
                  item: "https://keyzy.com/properties",
                },
                ...(searchTerm
                  ? [
                      {
                        "@type": "ListItem",
                        position: 3,
                        name: searchTerm.charAt(0).toUpperCase() + searchTerm.slice(1),
                        item: `https://keyzy.com/properties/${searchTerm}`,
                      },
                    ]
                  : []),
              ],
            }),
          }}
        />
      </Head>
      <PropertySearchPage
        searchTerm={search_term as string}
        hidePropertiesIfNoProperties={hasDevelopmentAd}
        status={status as string}
      />
      {!hasDevelopmentAd && <BenefitsOfKeyzyNow />}
      {!hasDevelopmentAd && isDevelopmentBookable && renderSignUpSection()}
      <div className="absolute hidden transform rotate-180 -left-10 -top-5 w-96 h-36 xl:inline-block">
        <Image
          src="/shared/shapes.svg"
          alt="shapes doodles"
          objectFit="contain"
          layout="fill"
          priority
        />
      </div>
      {renderLocationSpecificContent()}
      <div className="relative h-full bg-white w-full overflow-hidden mb-9 md:mb-0">
        <div className="flex w-full left-0 right-0 m overflow-hidden bg-repeat-x lg:mt-7 h-24 md:h-52 xl:h-48 bg-[url('/intermediaries/city-outline-mobile.svg')]  md:bg-[url('/intermediaries/city-outline-tablet.svg')] xl:bg-[url('/intermediaries/city-outline-desktop.svg')]" />
      </div>
      <div className="flex flex-col lg:flex-row">
        <div className="lg:w-1/2">
          <GotQuestions />
        </div>
        <div className="lg:w-1/2">
          <KnowYourBudget />
        </div>
      </div>
    </>
  );
};

Properties.getLayout = function getLayout(page: ReactElement) {
  return <MainLayout theme="light">{page}</MainLayout>;
};

export async function getServerSideProps(context: { params?: { search_term?: string } }) {
  const { search_term } = context.params || {};

  let hasNeighbourhoodContent = false;
  let hasDevelopmentAd = false;
  let ownYourFutureHome = "";
  let isDevelopmentBookable = false;
  if (search_term) {
    try {
      const response = await getNeighbourhood(search_term as NeighbourhoodSlug, ["developments"]);
      if (response.data && response.data.length > 0) {
        const neighbourhood = response.data[0];
        hasNeighbourhoodContent = neighbourhood.attributes.heading.trim() !== "";

        if (
          neighbourhood.attributes.developments &&
          neighbourhood.attributes.developments.data.length > 0
        ) {
          const development = neighbourhood.attributes.developments.data[0];
          isDevelopmentBookable = development.attributes.isBookable;
          hasDevelopmentAd = development.attributes.isUpcomingProject;
        }

        ownYourFutureHome = neighbourhood.attributes.ownYourFutureHome || "";
      }
    } catch (error) {
      console.error("Error fetching neighbourhood data:", error);
    }
  }

  return {
    props: {
      hasNeighbourhoodContent,
      hasDevelopmentAd,
      ownYourFutureHome,
      isDevelopmentBookable,
    },
  };
}

export default Properties;
